import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, UsersThree, ShieldCheck, SignIn } from "@phosphor-icons/react";

export default function Members() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { setListLoading(false); return; }
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, user, loading]);

  async function load() {
    setListLoading(true);
    const params = {};
    if (q) params.q = q;
    try {
      const { data } = await api.get("/members", { params });
      setItems(data);
    } catch (e) { /* noop */ }
    setListLoading(false);
  }

  if (!loading && !user) {
    return (
      <div className="pt-32 max-w-md mx-auto px-5 text-center" data-testid="members-page-locked">
        <UsersThree size={42} weight="duotone" className="text-[#F59E0B] mx-auto mb-3" />
        <h1 className="font-display text-3xl font-bold tracking-tight">Membros</h1>
        <p className="text-zinc-400 mt-2 mb-6">Tens de estar registado para ver os membros da comunidade.</p>
        <button onClick={() => navigate("/login")} className="btn-crimson inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold">
          <SignIn size={18} weight="bold" /> Entrar
        </button>
        <div className="mt-3 text-sm text-zinc-500">
          Sem conta? <Link to="/registar" className="text-[#E63946] hover:underline">Regista-te</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="members-page">
      <div className="mb-8">
        <div className="font-mono-accent text-xs text-[#F59E0B] mb-2">◢ COMUNIDADE</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Membros</h1>
        <p className="text-zinc-400 mt-2">Conhece os outros leitores de mangá em Portugal.</p>
      </div>

      <div className="relative mb-8 max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          placeholder="Pesquisar por nome..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 bg-[#121217] border-white/10 h-11"
          data-testid="members-search-input"
        />
      </div>

      {listLoading ? (
        <div className="text-center font-mono-accent text-xs text-zinc-500 py-10">A CARREGAR...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <UsersThree size={42} weight="duotone" className="text-[#F59E0B] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem membros encontrados</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(m => (
            <Link
              key={m.user_id}
              to={`/membros/${m.slug || m.user_id}`}
              data-testid={`member-${m.user_id}`}
              className="card-hover bg-[#121217] border border-white/5 rounded-2xl p-5 flex items-center gap-4"
            >
              {m.picture ? (
                <img src={resolveImageUrl(m.picture)} alt="" className="w-14 h-14 rounded-full object-cover border border-white/10 flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-xl font-display font-bold flex-shrink-0">
                  {(m.name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate flex items-center gap-2">
                  {m.name || "Sem nome"}
                  {m.role === "admin" && <ShieldCheck size={16} weight="fill" className="text-[#E63946]" title="Admin" />}
                </div>
                {m.bio && <div className="text-xs text-zinc-400 line-clamp-2 mt-1">{m.bio}</div>}
                <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">
                  desde {new Date(m.created_at).toLocaleDateString("pt-PT", { month: "short", year: "numeric" })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
